import { useEffect, useRef, useState } from 'react';

const REDUCED =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * A world-class, single-continuous-motion splash overlay for the WEB app —
 * a faithful port of the mobile SplashExperience. It sits on top of the live
 * app (which mounts underneath in parallel) and reveals it cinematically.
 * Total ~3s; respects Reduce Motion by simplifying to a short cross-fade.
 */
export default function SplashExperience({ onFinish }) {
  const [leaving, setLeaving] = useState(false);
  const finished = useRef(false);

  useEffect(() => {
    const leaveAt = REDUCED ? 1600 : 4200;
    const total = REDUCED ? 2100 : 4800;
    const t1 = setTimeout(() => setLeaving(true), leaveAt);
    const t2 = setTimeout(() => {
      if (!finished.current) {
        finished.current = true;
        onFinish();
      }
    }, total);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [onFinish]);

  return (
    <div
      className={`bh-splash${leaving ? ' bh-splash--leave' : ''}${REDUCED ? ' bh-splash--reduced' : ''}`}
      role="img"
      aria-label="Brotherhood+"
      data-testid="web-splash"
    >
      <style>{SPLASH_CSS}</style>

      <div className="bh-splash__watermark" aria-hidden="true">
        <div className="bh-splash__wm-drift">
          <span className="bh-splash__wm-text">B<span className="bh-splash__wm-plus">+</span></span>
        </div>
      </div>
      <div className="bh-splash__logo">
        <div className="bh-splash__breathe">
          <div className="bh-splash__lockup">
            <span className="bh-splash__word">
              <span className="bh-splash__wordtext">Brotherhood</span>
              <span className="bh-splash__wordglow" aria-hidden="true">Brotherhood</span>
              <span className="bh-splash__plus">+</span>
            </span>
            <span className="bh-splash__spark" aria-hidden="true" />
            <span className="bh-splash__sparkT bh-splash__sparkT--1" aria-hidden="true" />
            <span className="bh-splash__sparkT bh-splash__sparkT--2" aria-hidden="true" />
            <span className="bh-splash__sparkT bh-splash__sparkT--3" aria-hidden="true" />
          </div>
          <span className="bh-splash__tagline">Built with us in mind</span>
        </div>
      </div>
    </div>
  );
}

const SPLASH_CSS = `
.bh-splash {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #0B192C;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  opacity: 1;
  transition: opacity 0.56s cubic-bezier(0.25, 0.1, 0.25, 1);
}
.bh-splash--leave { opacity: 0; }

.bh-splash__watermark {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
  pointer-events: none;
  user-select: none;
  transition: opacity 0.56s ease;
}
.bh-splash--leave .bh-splash__watermark { opacity: 0; }
.bh-splash__wm-drift {
  animation: bhDrift 11s ease-in-out infinite alternate;
  will-change: transform;
}
.bh-splash__wm-text {
  display: block;
  font-family: 'Outfit', system-ui, sans-serif;
  font-weight: 800;
  font-size: clamp(300px, 42vw, 620px);
  line-height: 1;
  letter-spacing: -0.04em;
  color: #ffffff;
  opacity: 0;
  animation: bhWatermark 1.6s cubic-bezier(0.25, 0.1, 0.25, 1) 0.2s both;
}
.bh-splash__wm-plus { color: #C5A059; }

.bh-splash__logo {
  position: relative;
  z-index: 1;
  animation: bhFadeScale 1.2s cubic-bezier(0.25, 0.1, 0.25, 1) both;
  transition: transform 0.7s cubic-bezier(0.25, 0.1, 0.25, 1);
  will-change: transform, opacity;
}
.bh-splash--leave .bh-splash__logo { transform: translateY(-12%) scale(1); }

.bh-splash__breathe {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.bh-splash__lockup {
  display: flex;
  align-items: center;
  position: relative;
  overflow: visible;
  padding: 8px 6px;
}
.bh-splash__word {
  position: relative;
  color: #ffffff;
  font-family: 'Outfit', system-ui, sans-serif;
  font-weight: 700;
  font-size: 44px;
  letter-spacing: -0.5px;
  line-height: 1;
}
.bh-splash__wordtext {
  color: #ffffff;
}
/* Gold light that sweeps THROUGH the letters — an overlaid duplicate revealed by a
   moving mask band. The base white letters are ALWAYS solid, so nothing goes invisible. */
.bh-splash__wordglow {
  position: absolute;
  top: 0;
  left: 0;
  color: #FFEBB8;
  text-shadow: 0 0 14px rgba(255, 236, 190, 0.85), 0 0 5px rgba(255, 246, 220, 0.9);
  pointer-events: none;
  -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 42%, #000 58%, transparent 100%);
  mask-image: linear-gradient(90deg, transparent 0%, #000 42%, #000 58%, transparent 100%);
  -webkit-mask-size: 55% 100%;
  mask-size: 55% 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: -120% 0;
  mask-position: -120% 0;
  animation: bhWordGlow 1.3s linear 1.5s 1 both;
}
.bh-splash__plus {
  display: inline-block;
  color: #C5A059;
  opacity: 0.12;
  animation: bhPlusReveal 1s cubic-bezier(0.25, 0.1, 0.25, 1) 2.7s 1 both;
}

/* Disney-style gold "wand" spark: a single, continuous glide THROUGH the wordmark
   to the + (motion split from opacity so it never re-eases mid-flight — smooth). */
.bh-splash__spark {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 15px;
  height: 15px;
  margin: -7.5px 0 0 -7.5px;
  border-radius: 50%;
  background: radial-gradient(circle, #FFF7E4 0%, #F4D896 45%, rgba(197, 160, 89, 0) 72%);
  box-shadow: 0 0 20px 7px rgba(255, 236, 190, 0.75), 0 0 8px 2px rgba(255, 246, 220, 0.9);
  opacity: 0;
  z-index: 3;
  pointer-events: none;
  will-change: transform, opacity;
  animation: bhSparkMove 1.3s linear 1.5s 1 both,
             bhSparkFade 1.3s linear 1.5s 1 both;
}
/* Comet tail trailing to the left of the spark. */
.bh-splash__spark::before {
  content: '';
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 88px;
  height: 5px;
  border-radius: 3px;
  background: linear-gradient(90deg, rgba(255, 236, 190, 0) 0%, rgba(255, 236, 190, 0.45) 78%, rgba(255, 246, 220, 0.85) 100%);
  filter: blur(2px);
}
/* Faint fading sparkle trail — small dots lagging behind the spark on the same glide. */
.bh-splash__sparkT {
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  background: radial-gradient(circle, #FFF3D6 0%, rgba(244, 216, 150, 0.7) 55%, rgba(197, 160, 89, 0) 75%);
  opacity: 0;
  z-index: 2;
  pointer-events: none;
  will-change: transform, opacity;
}
.bh-splash__sparkT--1 { width: 9px; height: 9px; margin: -4.5px 0 0 -4.5px; box-shadow: 0 0 10px 3px rgba(255, 236, 190, 0.45); animation: bhSparkMove 1.3s linear 1.56s 1 both, bhSparkFade1 1.3s linear 1.56s 1 both; }
.bh-splash__sparkT--2 { width: 7px; height: 7px; margin: -3.5px 0 0 -3.5px; box-shadow: 0 0 8px 2px rgba(255, 236, 190, 0.35); animation: bhSparkMove 1.3s linear 1.62s 1 both, bhSparkFade2 1.3s linear 1.62s 1 both; }
.bh-splash__sparkT--3 { width: 5px; height: 5px; margin: -2.5px 0 0 -2.5px; box-shadow: 0 0 6px 2px rgba(255, 236, 190, 0.25); animation: bhSparkMove 1.3s linear 1.68s 1 both, bhSparkFade3 1.3s linear 1.68s 1 both; }

.bh-splash__tagline {
  margin-top: 14px;
  text-align: center;
  color: rgba(255, 255, 255, 0.55);
  font-family: 'Outfit', system-ui, sans-serif;
  font-size: 13px;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  font-weight: 600;
  animation: bhTagline 1s cubic-bezier(0.25, 0.1, 0.25, 1) 0.5s both;
}

@keyframes bhWatermark {
  0% { opacity: 0; transform: scale(1.08); }
  100% { opacity: 0.06; transform: scale(1); }
}
@keyframes bhDrift {
  0% { transform: translate(-20px, -12px); }
  100% { transform: translate(20px, 12px); }
}
@keyframes bhFadeScale {
  0% { opacity: 0; transform: scale(0.98); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes bhSparkMove {
  0%   { transform: translateX(-150px); }
  100% { transform: translateX(104px); }
}
@keyframes bhSparkFade {
  0%   { opacity: 0; }
  10%  { opacity: 1; }
  88%  { opacity: 1; }
  100% { opacity: 0; }
}
@keyframes bhSparkFade1 {
  0%   { opacity: 0; }
  12%  { opacity: 0.55; }
  86%  { opacity: 0.55; }
  100% { opacity: 0; }
}
@keyframes bhSparkFade2 {
  0%   { opacity: 0; }
  12%  { opacity: 0.4; }
  86%  { opacity: 0.4; }
  100% { opacity: 0; }
}
@keyframes bhSparkFade3 {
  0%   { opacity: 0; }
  12%  { opacity: 0.28; }
  86%  { opacity: 0.28; }
  100% { opacity: 0; }
}
@keyframes bhWordGlow {
  0%   { -webkit-mask-position: -120% 0; mask-position: -120% 0; }
  100% { -webkit-mask-position: 210% 0; mask-position: 210% 0; }
}
@keyframes bhPlusReveal {
  0%   { opacity: 0.12; color: #C5A059; text-shadow: none; transform: scale(0.9); }  28%  { opacity: 1; color: #FFF2D4; text-shadow: 0 0 26px rgba(255, 236, 190, 0.95), 0 0 10px rgba(255, 246, 220, 0.9); transform: scale(1.3); }
  100% { opacity: 1; color: #C5A059; text-shadow: 0 0 0 rgba(255, 236, 190, 0); transform: scale(1); }
}
@keyframes bhTagline {
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
}

.bh-splash--reduced .bh-splash__logo { animation: bhFadeScale 0.6s ease both; }
.bh-splash--reduced .bh-splash__spark,
.bh-splash--reduced .bh-splash__sparkT { display: none; }
.bh-splash--reduced .bh-splash__wordglow { display: none; }
.bh-splash--reduced .bh-splash__plus { opacity: 1; animation: none; }
.bh-splash--reduced.bh-splash--leave .bh-splash__logo { transform: none; }

@media (prefers-reduced-motion: reduce) {
  .bh-splash__spark,
  .bh-splash__sparkT { display: none; }
  .bh-splash__wordglow { display: none; }
  .bh-splash__plus { opacity: 1; animation: none; }
  .bh-splash__wm-drift { animation: none; }
}
`;
